<template>
  <v-container fluid class="fill-height justify-center pa-0">
    <v-row justify="center" align="center" class="no-gutters">
      <v-col cols="12">
        <v-card class="rounded-lg pa-6" elevation="2">
          <v-card-title class="d-flex flex-column align-start mb-4">
            <h2 class="text-h4 font-weight-bold primary--text">
              Secure Checkout
            </h2>
            <span class="text-subtitle-1 grey--text">
              Complete your purchase details below
            </span>
          </v-card-title>

          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
            @submit.prevent="purchase"
          >
            <div class="section-title mb-4">
              <v-icon left color="primary">mdi-account-circle-outline</v-icon>
              <span class="text-h6 font-weight-medium">Personal Details</span>
            </div>

            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="fullname"
                  label="Full Name"
                  placeholder="e.g. Johnny Depp"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account"
                  :rules="nameRules"
                  required
                  background-color="white"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="email"
                  label="Email Address"
                  placeholder="john@example.com"
                  outlined
                  dense
                  prepend-inner-icon="mdi-email"
                  :rules="emailRules"
                  required
                  background-color="white"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="phone"
                  label="Phone Number"
                  placeholder="(123) 456-7890"
                  outlined
                  dense
                  prepend-inner-icon="mdi-phone"
                  :rules="phoneRules"
                  required
                  background-color="white"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="zipCode"
                  :label="$t('Zip code')"
                  placeholder="10001"
                  outlined
                  dense
                  prepend-inner-icon="mdi-map-marker"
                  :rules="zipCodeRules"
                  required
                  background-color="white"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <div class="section-title mb-4">
              <v-icon left color="primary">mdi-credit-card-outline</v-icon>
              <span class="text-h6 font-weight-medium">Payment Method</span>
            </div>

            <div class="stripe-container mb-6">
              <div ref="payment" id="payment-element"></div>
            </div>

            <v-expand-transition>
              <v-alert
                v-if="cardErrors"
                type="error"
                dense
                text
                icon="mdi-alert-circle"
                class="mb-4"
              >
                {{ cardErrors }}
              </v-alert>
            </v-expand-transition>

            <slot />

            <v-row class="mt-4 align-center">
              <v-col cols="12" md="8" class="order-2 order-md-1">
                <div class="d-flex align-center grey--text text--darken-1">
                  <v-icon small class="mr-2" color="success">mdi-lock</v-icon>
                  <span class="text-caption mr-4 font-weight-medium"
                    >Payments are secure and encrypted</span
                  >
                </div>
                <div class="d-flex align-center mt-2">
                  <a
                    href="https://stripe.com"
                    target="_blank"
                    class="d-flex align-center text-decoration-none"
                  >
                    <span class="text-caption grey--text mr-2">Powered by</span>
                    <img
                      src="./powered_by_stripe.png"
                      alt="Stripe"
                      style="height: 22px; opacity: 0.8"
                    />
                  </a>
                  <span class="mx-2 grey--text text--lighten-1">|</span>
                  <a
                    href="https://stripe.com/legal"
                    target="_blank"
                    class="text-caption text-decoration-none grey--text hover-underline"
                    >Terms</a
                  >
                  <span class="mx-1 grey--text text--lighten-1">•</span>
                  <a
                    href="https://stripe.com/privacy"
                    target="_blank"
                    class="text-caption text-decoration-none grey--text hover-underline"
                    >Privacy</a
                  >
                </div>
              </v-col>

              <v-col cols="12" md="4" class="order-1 order-md-2 text-right">
                <payment-button
                  :loading="paymentProcessing"
                  @click="purchase"
                />
                <!--                <v-btn-->
                <!--                  type="submit"-->
                <!--                  :loading="paymentProcessing"-->
                <!--                  :disabled="paymentProcessing"-->
                <!--                  color="primary"-->
                <!--                  large-->
                <!--                  block-->
                <!--                  elevation="0"-->
                <!--                  class="text-capitalize font-weight-bold"-->
                <!--                >-->
                <!--                  {{ buttonText || 'Pay Now' }}-->
                <!--                </v-btn>-->
              </v-col>
            </v-row>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
/* global Stripe */
import formRules from '../mixins/form-rules-mixin'
import PaymentButton from 'components/stripe/payment-button.vue'
export default {
  name: 'StripePaymentPage',
  mixins: [formRules],
  components: {
    PaymentButton,
  },
  props: {
    clientSecret: { type: String, required: true },
    buttonText: { type: String, default: 'Pay Now' },
    publicKey: { type: String, required: true },
    reservation: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      stripe: null,
      elements: null,
      paymentElement: null,
      valid: false,
      fullname: '',
      phone: '',
      zipCode: '',
      email: '',
      cardErrors: '',
      paymentProcessing: false,
    }
  },
  mounted() {
    this.initializeStripe()
    this.prefillData()
  },
  computed: {
    guest() {
      return this.reservation?.guest
    },
  },
  methods: {
    prefillData() {
      if (this.guest) {
        this.fullname = this.guest.full_name || ''
        this.phone = this.guest.primary_phone || this.guest.phones?.[0] || ''
        this.email = this.guest.primary_email || this.guest.emails?.[0] || ''
      }
    },
    initializeStripe() {
      if (!window.Stripe) {
        console.error('Stripe.js not loaded')
        return
      }

      this.stripe = Stripe(this.publicKey)

      // Styling the Stripe Iframe to match Vuetify
      // This is the "Appearance API"
      const appearance = {
        theme: 'stripe',
        variables: {
          colorPrimary: '#635bff',
          colorText: '#30313d',
          colorDanger: '#df1b41',
          fontFamily: '"Roboto", system-ui, sans-serif',
          spacingUnit: '4px',
          borderRadius: '6px',
        },
        rules: {
          '.Input': {
            border: '1px solid #e6e6e6',
            boxShadow:
              '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02)',
            padding: '12px',
            transition: 'box-shadow 0.2s ease',
          },
          '.Input:focus': {
            border: '1px solid #635bff',
            boxShadow:
              '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02), 0 0 0 4px rgba(99, 91, 255, 0.15)',
          },
          '.Label': {
            fontWeight: '500',
            color: '#30313d',
            marginBottom: '6px',
            fontSize: '0.93rem',
          },
          '.Tab': {
            border: '1px solid #e6e6e6',
            boxShadow:
              '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02)',
          },
          '.Tab:hover': {
            color: '#635bff',
          },
          '.Tab--selected': {
            borderColor: '#635bff',
            boxShadow:
              '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02), 0 0 0 4px rgba(99, 91, 255, 0.15)',
          },
        },
      }

      this.elements = this.stripe.elements({
        clientSecret: this.clientSecret,
        appearance,
      })

      this.paymentElement = this.elements.create('payment', {
        layout: 'tabs', // 'tabs' or 'accordion' looks very modern
      })

      this.paymentElement.mount(this.$refs.payment)
    },
    async purchase() {
      // 1. Validate Vuetify Form (Personal Details)
      const isFormValid = this.$refs.form.validate()
      if (!isFormValid) return

      // 2. Clear previous errors and start loading
      this.cardErrors = ''
      this.paymentProcessing = true
      this.$emit('payment-processing')

      // 3. Confirm Payment via Stripe
      // Note: confirmPayment is the modern equivalent of confirmCardPayment
      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          // Important: Where to go after payment (if 3D secure is needed)
          // You might need to adjust this URL based on your router
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: this.fullname,
              email: this.email,
              phone: this.phone,
              address: {
                postal_code: this.zipCode,
              },
            },
          },
        },
        // If you handle redirection manually or rely on single-page behavior:
        redirect: 'if_required',
      })

      if (error) {
        // Show error to your customer (e.g., insufficient funds)
        this.cardErrors = error.message
        this.paymentProcessing = false
        this.$emit('payment-failed', error)
      } else {
        // Payment succeeded
        this.$emit('payment-success', {
          user: { email: this.email },
        })
        this.paymentProcessing = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.bg-gray-50 {
  background-color: #f9fafb; /* Light grey background for the page */
}

/* We don't need much custom CSS for the inputs anymore
   because we handled it in the `appearance` JS object,
   but we can add a wrapper style for the stripe element.
*/
.stripe-container {
  min-height: 150px; /* Prevents layout jump before Stripe loads */
}

.section-title {
  display: flex;
  align-items: center;
  color: #333;
}

/* Optional: Tweaks for specific Vuetify overrides if needed */
::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(0, 0, 0, 0.2);
}
</style>
